/* eslint-disable @typescript-eslint/no-explicit-any */
import { DashOutlined } from '@ant-design/icons'
import { SorterResult } from 'antd/es/table/interface'
import { IAccount } from 'app/api/account/model'
import R from 'app/assets/R'
import { ENV_CONFIG } from 'app/common/config'
import { PermissionEnum } from 'app/common/enum'
import { ISort } from 'app/common/interface'
import BaseAvatarProfile from 'app/components/common/BaseAvatarProfile'
import { BaseCol } from 'app/components/common/BaseCol'
import { BasePopover } from 'app/components/common/BasePopover'
import { BaseRow } from 'app/components/common/BaseRow'
import { BaseSpace } from 'app/components/common/BaseSpace'
import BaseText from 'app/components/common/BaseText'
import { GenerateWorkInfo } from 'app/components/header/components/profile/ProfileDropdown'
import { convertColumnTable } from 'app/components/tables/BaseTableReport/hook'
import { useVerifyPermission } from 'app/helpers/permission.helper'
import { useDebounce, usePagination } from 'app/hook'
import { useGetAccountList } from 'app/react-query/hook/account'
import { IResponseQueryList } from 'app/react-query/model/common'
import { handleDownloadExcel } from 'app/service/general/service'
import { convertedVariables } from 'app/styles/themes/themeVariables'
import {
  BORDER_RADIUS,
  PADDING,
  Pagination,
  initialPagination,
} from 'parkway-web-common'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  ChangePasswordModalLayout,
  DeleteModalLayout,
  UpdateModalLayout,
} from './layouts'
import { IFilterAccountList } from './type'
export const useHook = () => {
  const { t } = useTranslation()
  const [filter, setFilter] = useState<IFilterAccountList>({})

  const { user, verifyPermission } = useVerifyPermission()
  const [sorts, setSort] = useState<ISort[]>([])

  const [pagination, setPagination] = useState<Pagination>(initialPagination)
  const filterDebounce = useDebounce(filter, 500)

  const { flattenDataList } = usePagination()
  const { data, isLoading } = useGetAccountList({
    limit: pagination?.pageSize,
    page: pagination?.current,
    roleIds: filterDebounce?.roleIds,

    search: filterDebounce?.search,
    is_active: filterDebounce?.status,
    sorts,
  })

  const flattenData = useMemo(() => {
    const res: IResponseQueryList<IAccount[]> = flattenDataList(data)

    return res
  }, [data])

  const dataTable = useMemo(() => {
    return flattenData?.data
  }, [flattenData])

  const total = useMemo(() => {
    return flattenData?.total ?? dataTable?.length
  }, [flattenData])

  const isHavePermissionUpdateAccount = useMemo(() => {
    return verifyPermission(PermissionEnum.UpdateAccount)
  }, [user])

  const isHavePermissionChangePassword = useMemo(() => {
    return verifyPermission(PermissionEnum.ChangePassword)
  }, [user])

  const isHavePermissionDeleteAccount = useMemo(() => {
    return verifyPermission(PermissionEnum.DeleteAccount)
  }, [user])

  const columns = useMemo(() => {
    return [
      convertColumnTable<IAccount>({
        key: 'full_name',
        title: t(R.strings.full_name),
        classNameWidthColumnOverwrite: 'very-big-column',
        fixed: 'left',
        sorter: true,
        render: (_, record) => {
          return (
            <BaseAvatarProfile
              name={record?.full_name}
              jobTitle={GenerateWorkInfo({
                isTradeUnionMember: record?.isTradeUnionMember,
                roles: record?.roles,
              })}
              src={record?.avatar}
            />
          )
        },
      }),
      convertColumnTable<IAccount>({
        key: 'phone',
        title: t(R.strings.phone),
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.phone ?? '-'}
            />
          )
        },
      }),
      convertColumnTable<IAccount>({
        key: 'email',
        title: t(R.strings.email),

        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.email ?? '-'}
            />
          )
        },
      }),
      convertColumnTable<IAccount>({
        key: 'roles',
        title: t(R.strings.role),
        classNameWidthColumnOverwrite: 'very-big-column',
        render: (_, record) => {
          return (
            <BaseRow gutter={[8, 8]}>
              {record?.roles?.map((item, index) => {
                return (
                  <BaseCol key={index}>
                    <RoleItem key={index}>
                      <BaseText children={item?.name} />
                    </RoleItem>
                  </BaseCol>
                )
              })}
            </BaseRow>
          )
        },
      }),

      convertColumnTable<IAccount>({
        key: 'is_active',
        title: t(R.strings.status),
        render: (_, record) => {
          return (
            <StatusWrapper $isActive={!!record?.is_active}>
              <BaseText
                children={
                  record?.is_active
                    ? t(R.strings.active_content)
                    : t(R.strings.in_active)
                }
                colorText={
                  record?.is_active ? 'statesGreenColor' : 'statesRedColor'
                }
                fontWeight="medium"
              />
            </StatusWrapper>
          )
        },
      }),

      convertColumnTable<IAccount>({
        key: 'is_active',
        title: '',
        fixed: 'right',
        classNameWidthColumnOverwrite: 'number-column',
        render: (_, record) => {
          return (
            <BasePopover
              trigger="click"
              content={
                <BaseSpace>
                  {isHavePermissionUpdateAccount ? (
                    <UpdateModalLayout account={record} />
                  ) : null}

                  {isHavePermissionChangePassword ? (
                    <ChangePasswordModalLayout account={record} />
                  ) : null}

                  {isHavePermissionDeleteAccount ? (
                    <DeleteModalLayout
                      id={record?.id}
                      name={record?.full_name}
                    />
                  ) : null}
                </BaseSpace>
              }
            >
              <DashOutlined rev={undefined} rotate={90} />
            </BasePopover>
          )
        },
      }),
    ]
  }, [t])

  const handleTableChange = (
    pagination: Pagination,
    _,
    sorter: SorterResult<any> | SorterResult<any>[],
  ) => {
    setPagination(pagination)

    let sortParams: { field: string; order: 'ASC' | 'DESC' }[] = []

    if (Array.isArray(sorter)) {
      // Nếu sorter là mảng (multi-sort)
      sortParams = sorter
        .filter(s => s.order) // Lọc ra những cột có thứ tự sắp xếp
        .map(s => ({
          field: s.field as string,
          order: s.order === 'ascend' ? 'ASC' : 'DESC',
        }))
    } else if (sorter.order) {
      // Nếu chỉ có một cột được sort
      sortParams = [
        {
          field: sorter.field as string,
          order: sorter.order === 'ascend' ? 'ASC' : 'DESC',
        },
      ]
    }

    setSort(sortParams)
  }

  const onChangeFilter = (newFilter: IFilterAccountList) => {
    setFilter(newFilter)
    setPagination(initialPagination)
  }

  const downloadExcel = async (): Promise<void> => {
    await handleDownloadExcel({
      params: {
        roleIds: filterDebounce?.roleIds,
        search: filterDebounce?.search,
        is_active: filterDebounce?.status,
      },
      urlMethod: `${ENV_CONFIG.API_ENDPOINT}admin/account/download`,
    })
  }

  return {
    isLoading,
    columns,
    pagination: {
      ...pagination,
      total,
    },
    dataTable,
    handleTableChange,
    onChangeFilter,
    filter,
    downloadExcel,
  }
}

const StatusWrapper = styled.div<{ $isActive?: boolean }>`
  padding: 3px ${PADDING.xxs};
  border: 1px solid
    ${props =>
      props?.$isActive
        ? convertedVariables?.statesGreenColor
        : convertedVariables.statesRedColor};

  background-color: ${props =>
    props?.$isActive
      ? convertedVariables?.statesGreenLightColor
      : convertedVariables.statesRedLightColor};

  border-radius: ${BORDER_RADIUS};
  width: fit-content;
`

const RoleItem = styled.div`
  background-color: ${convertedVariables?.neutralBlack9Color};
  border-radius: ${PADDING.xl};
  padding: 5px 16px;
`
